import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';
import { Button, cx, DataTable, type DataColumn } from '../ui';
import s from './Charts.module.css';

// Insights charts (APP-045 amendment A6): hand-built from HTML, CSS and a little SVG, coloured with
// design tokens only. Geometry is set through React's `style` (the CSS object model), which the
// strict CSP allows; no inline style attribute is ever written into markup. Every chart is
// decorative to assistive technology and comes with a one-line summary and a table view.

export type ChartTone = 'navy' | 'petrol' | 'cyan' | 'sky' | 'mist' | 'gold' | 'track';

const TONE: Record<ChartTone, string | undefined> = {
  navy: s.navy,
  petrol: s.petrol,
  cyan: s.cyan,
  sky: s.sky,
  mist: s.mist,
  gold: s.gold,
  track: s.trackTone,
};

/** A round top for the axis: 1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8 × 10ⁿ at or above the largest value. */
export function niceMax(max: number): number {
  if (!(max > 0)) return 1;
  const step = 10 ** Math.floor(Math.log10(max));
  for (const m of [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) if (m * step >= max) return m * step;
  return 10 * step;
}

/** A share of the axis as a CSS percentage, never past the ends. */
export function pct(value: number, max: number): string {
  if (!(max > 0)) return '0%';
  return `${String(Math.max(0, Math.min(100, (value / max) * 100)))}%`;
}

// --- The chart / table switch ---------------------------------------------------------------------

/** "View as table" / "View as chart" for a card's heading row. */
export function ViewToggle({
  asTable,
  onChange,
}: {
  asTable: boolean;
  onChange: (asTable: boolean) => void;
}): ReactElement {
  return (
    <Button
      variant="ghost"
      size="sm"
      onPress={() => {
        onChange(!asTable);
      }}
    >
      {asTable ? 'View as chart' : 'View as table'}
    </Button>
  );
}

export interface ChartTableProps<Row> {
  caption: string;
  columns: readonly DataColumn<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  empty: { title: string; body: string };
}

/** The table twin of a chart: the same numbers, every one readable. */
export function ChartTable<Row>(props: ChartTableProps<Row>): ReactElement {
  return <DataTable {...props} />;
}

/**
 * The plot area and its sentence. A drawn plot (columns, lines) is hidden from assistive
 * technology and the sentence and table say it in words. `readable` plots (bar rows) keep their
 * labels and values readable, and links in them usable; only their bars are hidden.
 */
export function ChartFigure({
  summary,
  children,
  className,
  readable = false,
}: {
  summary: string;
  children: ReactNode;
  className?: string;
  readable?: boolean;
}): ReactElement {
  return (
    <figure className={cx(s.figure, className)}>
      {readable ? children : <div aria-hidden="true">{children}</div>}
      <figcaption className={s.summary}>{summary}</figcaption>
    </figure>
  );
}

/** A designed empty chart: the ghost of the plot, greyed, with what will appear and when. */
export function GhostOverlay({
  headline,
  body,
  children,
  placement = 'center',
}: {
  headline: string;
  body: string;
  children: ReactNode;
  /**
   * `center` lays the note over a column chart's plot, clear of its axis and month labels;
   * `above` puts it over bar rows, whose names and values sit beside the bars and stay readable.
   */
  placement?: 'center' | 'above';
}): ReactElement {
  const id = useId();
  return (
    <div
      className={cx(s.ghost, placement === 'above' && s.ghostAbove)}
      role="group"
      aria-labelledby={id}
    >
      <div className={s.ghostPlot} aria-hidden="true">
        {children}
      </div>
      <div className={s.ghostNote}>
        <p id={id} className={s.ghostHeadline}>
          {headline}
        </p>
        <p className={s.ghostBody}>{body}</p>
      </div>
    </div>
  );
}

// --- Columns (one value per month) ----------------------------------------------------------------

export interface ColumnDatum {
  key: string;
  /** Under the column ("Jan"). */
  label: string;
  value: number;
  /** The value in words, for the hover note and the labelled columns. */
  display: string;
}

const GHOST_HEIGHTS = [22, 34, 70, 36, 30, 58, 26, 33, 74, 44, 52, 40];
const GHOST_WIDTHS = [62, 48, 70, 40, 55, 34];

/**
 * Vertical columns on one axis with three gridlines. By default labels sit only on the highest
 * column and the last one (selective direct labels, for a run of months); `labels="all"` labels
 * every column above zero, for a short set of categories. Every value is in the table view and the
 * hover note.
 */
export function Columns({
  data,
  tone = 'navy',
  tick,
  ghost = false,
  labels = 'peaks',
  ghostMax,
}: {
  data: readonly ColumnDatum[];
  tone?: ChartTone;
  /** Axis tick text from a value. */
  tick: (value: number) => string;
  /** Draw placeholder columns (a designed empty state sits on top). */
  ghost?: boolean;
  /** Which columns carry their value: the highest and the last, or every one. */
  labels?: 'peaks' | 'all';
  /**
   * The empty chart's axis: a nominal top in the chart's own unit (a month's share of the budget,
   * a handful of awards), so the ghost still reads "$60K · $30K · $0" rather than "$0 · $0 · $0".
   * Without it a ghost leaves its axis words out.
   */
  ghostMax?: number;
}): ReactElement {
  const max =
    ghost && ghostMax !== undefined
      ? niceMax(ghostMax)
      : niceMax(Math.max(0, ...data.map((d) => d.value)));
  const axis = !ghost || ghostMax !== undefined;
  const top = data.reduce((best, d, i) => ((data[best]?.value ?? 0) < d.value ? i : best), 0);
  const last = data.length - 1;
  return (
    <div className={s.columns}>
      <div className={s.yAxis}>
        <span>{axis ? tick(max) : ''}</span>
        <span>{axis ? tick(max / 2) : ''}</span>
        <span>{axis ? tick(0) : ''}</span>
      </div>
      <div className={s.plot}>
        <div className={s.grid}>
          <span />
          <span />
          <span />
        </div>
        <div className={s.columnRow}>
          {data.map((d, i) => (
            <div
              key={d.key}
              className={s.slot}
              title={ghost ? undefined : `${d.label}: ${d.display}`}
            >
              <div className={s.columnTrack}>
                {!ghost && d.value > 0 && (labels === 'all' || i === top || i === last) && (
                  <span className={s.columnLabel}>{d.display}</span>
                )}
                <span
                  className={cx(s.column, ghost ? s.ghostTone : TONE[tone])}
                  style={{
                    height: ghost
                      ? `${String(GHOST_HEIGHTS[i % GHOST_HEIGHTS.length] ?? 30)}%`
                      : pct(d.value, max),
                  }}
                />
              </div>
              <span className={s.xLabel}>{d.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// --- Bar rows (one row per pool, sector, person) --------------------------------------------------

export interface BarSegment {
  value: number;
  tone: ChartTone;
}

export interface Bar {
  segments: readonly BarSegment[];
  /** A lighter track behind the segments (a budget), on the same scale. */
  track?: number | null;
  /** Words after the bar ("$175.5K"). */
  label?: string;
}

export interface BarRowDatum {
  key: string;
  label: ReactNode;
  sub?: ReactNode;
  bars: readonly Bar[];
  /** The row's numbers in words, at its right. */
  value?: ReactNode;
}

/** Horizontal bars on one shared scale; segments stack with a 2 px gap, a track shows a budget. */
export function BarRows({
  rows,
  max,
  ticks,
  ghost = false,
  reference,
  compact = false,
  stacked = false,
  tight = false,
}: {
  rows: readonly BarRowDatum[];
  max: number;
  /** Axis ticks under the bars, as words. */
  ticks?: readonly { value: number; label: string }[];
  ghost?: boolean;
  /** A vertical rule, such as the company average, named over the bars ("Company 7.4"). */
  reference?: { value: number; label: string };
  compact?: boolean;
  /** Name and value on one line, the bar under them: for narrow side cards. */
  stacked?: boolean;
  /** Name, bar and value on one row, even in a narrow card or on a phone (short names). */
  tight?: boolean;
}): ReactElement {
  return (
    <div
      className={cx(
        s.barRows,
        compact && s.barRowsCompact,
        stacked && s.barRowsStacked,
        tight && s.barRowsTight,
      )}
    >
      {reference !== undefined && !ghost && (
        <div className={s.barRefRow} aria-hidden="true">
          <span />
          <div className={s.barRefArea}>
            <span className={s.referenceLabel} style={{ left: pct(reference.value, max) }}>
              {reference.label}
            </span>
          </div>
          <span />
        </div>
      )}
      {rows.map((row, r) => (
        <div key={row.key} className={s.barRow}>
          <div className={s.barLabel}>
            <span className={s.barName}>{row.label}</span>
            {row.sub !== undefined && <span className={s.barSub}>{row.sub}</span>}
          </div>
          <div className={s.barStack} aria-hidden="true">
            {row.bars.map((bar, b) => (
              <div key={b} className={s.barLine}>
                <div className={s.barArea}>
                  {bar.track !== undefined && bar.track !== null && (
                    <span
                      className={cx(s.track, ghost && s.ghostTone)}
                      style={{ width: pct(bar.track, max) }}
                    />
                  )}
                  <span className={s.segments}>
                    {ghost ? (
                      // A placeholder length, so the empty chart still reads as a chart.
                      <span
                        className={cx(s.segment, s.ghostTone)}
                        style={{
                          width: `${String(GHOST_WIDTHS[(r + b) % GHOST_WIDTHS.length] ?? 40)}%`,
                        }}
                      />
                    ) : (
                      bar.segments
                        .filter((seg) => seg.value > 0)
                        .map((seg, i) => (
                          <span
                            key={i}
                            className={cx(s.segment, TONE[seg.tone])}
                            style={{ width: pct(seg.value, max) }}
                          />
                        ))
                    )}
                  </span>
                  {reference !== undefined && (
                    <span className={s.reference} style={{ left: pct(reference.value, max) }} />
                  )}
                </div>
                {bar.label !== undefined && <span className={s.barValueLabel}>{bar.label}</span>}
              </div>
            ))}
          </div>
          {row.value !== undefined && <div className={s.barValue}>{row.value}</div>}
        </div>
      ))}
      {ticks !== undefined && ticks.length > 0 && (
        <div className={s.barAxis} aria-hidden="true">
          <span />
          <div className={s.barTicks}>
            {ticks.map((t) => (
              <span key={t.value} style={{ left: pct(t.value, max) }}>
                {t.label}
              </span>
            ))}
          </div>
          <span />
        </div>
      )}
    </div>
  );
}

/** Three ticks for a bar axis: 0, half and the round top. */
export function barTicks(max: number, format: (value: number) => string) {
  return [0, max / 2, max].map((value) => ({ value, label: format(value) }));
}

// --- Lines (cumulative pace) ----------------------------------------------------------------------

export interface LineSeries {
  key: string;
  tone: ChartTone;
  /** One value per month on the chart's scale; null where there's none yet. */
  values: readonly (number | null)[];
  /** The direct label at the line's last point ("2026 · 39% on Oct 14"). */
  endLabel: string;
}

/** A line chart's vertical scale: the least top it shows, its tick words and how many steps. */
export interface LineScale {
  /** The top is at least this; above it, the next round number over the data. */
  floor: number;
  tick: (value: number) => string;
  /** Gridline steps between 0 and the top. */
  steps: number;
}

/** A running share of a budget: 0 to 100%, or higher when a year went over. */
export const PERCENT_SCALE: LineScale = {
  floor: 100,
  tick: (v) => `${String(Math.round(v))}%`,
  steps: 4,
};

/** The plot's width before the first measurement (and in tests, which lay nothing out). */
const FALLBACK_WIDTH = 600;
const H = 220;
const PAD = { top: 16, right: 16, bottom: 26, left: 44 };
/** About how wide a month label is at the tick size: 11 px type, three letters and a gap. */
export const MONTH_LABEL_PX = 30;

/**
 * How often to write an axis label so none touch: 1 for every label, 2 for every second, and so
 * on. The longest label decides, at MONTH_LABEL_PX for three characters, against the room each
 * label has (`slotPx`). Ask the data's charts thin theirs the same way (APP-192, AIE-046).
 */
export function labelEvery(labels: readonly string[], slotPx: number): number {
  const longest = Math.max(1, ...labels.map((l) => l.length));
  return Math.max(1, Math.ceil((MONTH_LABEL_PX * (longest / 3)) / Math.max(1, slotPx)));
}

/**
 * The element's width in CSS pixels, kept current. The chart draws in these pixels, so its words
 * stay at their set size on a phone and on a wide screen (an SVG scaled to fit shrinks them).
 */
export function useWidth(ref: RefObject<Element | null>): number {
  const [width, setWidth] = useState(FALLBACK_WIDTH);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const measure = () => {
      const next = Math.round(el.getBoundingClientRect().width);
      if (next > 0) setWidth(next);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [ref]);
  return width;
}

/**
 * Lines over a year's months on one axis, drawn at the plot's own width so every word keeps its
 * size (APP-045 A55). Month names thin out to every second or third when they'd touch.
 */
export function Lines({
  months,
  series,
  scale,
  ghost = false,
}: {
  /** The year's short month names, from its first month (twelve, or thirteen from a mid-month start). */
  months: readonly string[];
  series: readonly LineSeries[];
  scale: LineScale;
  ghost?: boolean;
}): ReactElement {
  const box = useRef<HTMLDivElement>(null);
  const W = useWidth(box);
  const top = Math.max(
    scale.floor,
    ...series.flatMap((x) => x.values.filter((v): v is number => v !== null)),
  );
  const max = top > scale.floor ? niceMax(top) : scale.floor;
  const span = Math.max(1, months.length - 1);
  const x = (i: number) => PAD.left + (i * (W - PAD.left - PAD.right)) / span;
  const y = (v: number) => PAD.top + (1 - v / max) * (H - PAD.top - PAD.bottom);
  const ticks = Array.from({ length: scale.steps + 1 }, (_, i) => (max * i) / scale.steps);
  const every = labelEvery(months, (W - PAD.left - PAD.right) / span);
  return (
    <div ref={box} className={s.linesBox}>
      <svg
        className={s.lines}
        width={W}
        height={H}
        viewBox={`0 0 ${String(W)} ${String(H)}`}
        role="presentation"
        focusable="false"
      >
        {ticks.map((t) => (
          <g key={t}>
            <line className={s.lineGrid} x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} />
            <text className={s.lineTick} x={PAD.left - 8} y={y(t) + 4} textAnchor="end">
              {scale.tick(t)}
            </text>
          </g>
        ))}
        {months.map((m, i) =>
          i % every === 0 ? (
            <text key={m + String(i)} className={s.lineTick} x={x(i)} y={H - 6} textAnchor="middle">
              {m}
            </text>
          ) : null,
        )}
        {series.map((line) => {
          const points = line.values
            .map((v, i) => (v === null ? null : { x: x(i), y: y(v) }))
            .filter((p): p is { x: number; y: number } => p !== null);
          const end = points.at(-1);
          const flip = end !== undefined && end.x > W - 160;
          return (
            <g key={line.key} className={ghost ? s.ghostLine : TONE[line.tone]}>
              <polyline
                className={s.linePath}
                points={points.map((p) => `${String(p.x)},${String(p.y)}`).join(' ')}
              />
              {end !== undefined && (
                <>
                  <circle className={s.lineDot} cx={end.x} cy={end.y} r={5} />
                  {!ghost && (
                    <text
                      className={s.lineLabel}
                      x={flip ? end.x - 10 : end.x + 10}
                      y={end.y - 10}
                      textAnchor={flip ? 'end' : 'start'}
                    >
                      {line.endLabel}
                    </text>
                  )}
                </>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** RPT3's pace: lines of a running share of each year's budget. */
export function PaceLines(props: {
  months: readonly string[];
  series: readonly LineSeries[];
  ghost?: boolean;
}): ReactElement {
  return <Lines {...props} scale={PERCENT_SCALE} />;
}

// --- Legend ---------------------------------------------------------------------------------------

/** The key for two or more series: a swatch and its words (identity never by colour alone). */
export function Legend({
  items,
}: {
  items: readonly { key: string; label: string; tone: ChartTone; line?: boolean }[];
}): ReactElement {
  return (
    <ul className={s.legend}>
      {items.map((item) => (
        <li key={item.key}>
          <span
            className={cx(item.line ? s.swatchLine : s.swatch, TONE[item.tone])}
            aria-hidden="true"
          />
          {item.label}
        </li>
      ))}
    </ul>
  );
}
